import React, { useMemo, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';
import type { Subscription, Invoice } from '../../../types/subscriptionManagement';
import { toInr, loadFxRates } from '../../../utils/fxUtils';
import {
  allocatePaidInvoiceInrToMonths,
  hasPaidExpenseInMonths,
  isPaidExpenseInvoice,
} from '../../../utils/subscriptionExpenseAllocation';
import { fyMonths } from '../../../utils/subscriptionFyUtils';
import { DepartmentBreakdownChart } from './charts/DepartmentBreakdownChart';
import { MonthlySpendTrendChart } from './charts/MonthlySpendTrendChart';
import { CategoryAnalysisChart } from './charts/CategoryAnalysisChart';
import { TopSubscriptionsChart } from './charts/TopSubscriptionsChart';
import { SubscriptionPeriodGanttChart } from './charts/SubscriptionPeriodGanttChart';
import { buildSubscriptionTimeline } from '../../../utils/subscriptionTimeline';

interface Props {
  subscriptions: Subscription[];
  /** FY-scoped invoices (expense charts). */
  invoices: Invoice[];
  /** All invoices — used for coverage timeline segments. */
  allInvoices: Invoice[];
  fyStart: number | 'all';
}

export const AnalyticsSection: React.FC<Props> = ({
  subscriptions,
  invoices,
  allInvoices,
  fyStart,
}) => {
  useEffect(() => {
    loadFxRates();
  }, []);

  const allowedMonths = useMemo(() => {
    if (fyStart === 'all') return null;
    return new Set(fyMonths(fyStart));
  }, [fyStart]);

  const subMap = useMemo(
    () => new Map(subscriptions.map(s => [s.id, s])),
    [subscriptions],
  );

  const paidInvoices = useMemo(
    () => invoices.filter(isPaidExpenseInvoice),
    [invoices],
  );

  const deptData = useMemo(() => {
    const map: Record<string, number> = {};
    paidInvoices.forEach(inv => {
      const sub = subMap.get(inv.subscriptionId);
      const dept = sub?.department || 'Other';
      const inr = toInr(Number(inv.amount) || 0, inv.currency || 'INR', {
        invoiceDate: inv.invoiceDate,
      });
      const buckets = allocatePaidInvoiceInrToMonths(inv, inr);
      for (const [ym, portion] of Object.entries(buckets)) {
        if (allowedMonths && !allowedMonths.has(ym)) continue;
        map[dept] = (map[dept] || 0) + portion;
      }
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [paidInvoices, subMap, allowedMonths]);

  const catData = useMemo(() => {
    const map: Record<string, number> = {};
    paidInvoices.forEach(inv => {
      const sub = subMap.get(inv.subscriptionId);
      const cat = sub?.category || 'Other';
      const inr = toInr(Number(inv.amount) || 0, inv.currency || 'INR', {
        invoiceDate: inv.invoiceDate,
      });
      const buckets = allocatePaidInvoiceInrToMonths(inv, inr);
      for (const [ym, portion] of Object.entries(buckets)) {
        if (allowedMonths && !allowedMonths.has(ym)) continue;
        map[cat] = (map[cat] || 0) + portion;
      }
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [paidInvoices, subMap, allowedMonths]);

  const monthlyTrend = useMemo(() => {
    const map: Record<string, number> = {};
    paidInvoices.forEach(inv => {
      const inr = toInr(Number(inv.amount) || 0, inv.currency || 'INR', {
        invoiceDate: inv.invoiceDate,
      });
      const buckets = allocatePaidInvoiceInrToMonths(inv, inr);
      for (const [ym, portion] of Object.entries(buckets)) {
        if (allowedMonths && !allowedMonths.has(ym)) continue;
        map[ym] = (map[ym] || 0) + portion;
      }
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ym, value]) => {
        const [y, m] = ym.split('-');
        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return { label: `${MONTHS[Number(m) - 1]} '${y.slice(2)}`, value: Math.round(value) };
      });
  }, [paidInvoices, allowedMonths]);

  const topSubs = useMemo(() => {
    const spend: Record<string, number> = {};
    paidInvoices.forEach(inv => {
      const id = inv.subscriptionId;
      const inr = toInr(Number(inv.amount) || 0, inv.currency || 'INR', {
        invoiceDate: inv.invoiceDate,
      });
      const buckets = allocatePaidInvoiceInrToMonths(inv, inr);
      let subTotal = 0;
      for (const [ym, portion] of Object.entries(buckets)) {
        if (allowedMonths && !allowedMonths.has(ym)) continue;
        subTotal += portion;
      }
      spend[id] = (spend[id] || 0) + subTotal;
    });
    return Object.entries(spend)
      .map(([id, value]) => ({
        name: subMap.get(id)?.name || id,
        value: Math.round(value),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [paidInvoices, subMap, allowedMonths]);

  const hasPaidData = hasPaidExpenseInMonths(invoices, allowedMonths);

  const timelineModel = useMemo(
    () => buildSubscriptionTimeline(subscriptions, allInvoices, fyStart),
    [subscriptions, allInvoices, fyStart],
  );

  if (subscriptions.length === 0) return null;

  return (
    <div className="space-y-4">
      {timelineModel && <SubscriptionPeriodGanttChart model={timelineModel} />}

      {hasPaidData ? (
        <>
          <div className="flex items-center gap-2">
        <div className="w-1 h-4 rounded-full bg-emerald-500 flex-none" />
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
          Expense Analytics
        </h3>
        <span className="text-[10px] text-slate-400">(paid only · coverage-prorated)</span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <DepartmentBreakdownChart data={deptData} />
        <MonthlySpendTrendChart data={monthlyTrend} />
        <CategoryAnalysisChart data={catData} />
        <TopSubscriptionsChart data={topSubs} />
      </div>
        </>
      ) : (
        !timelineModel && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm py-10 text-center">
            <BarChart3 size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium text-slate-500">No paid expense data</p>
            <p className="text-xs text-slate-400 mt-1">
              Mark invoices as Paid to see spend charts.
            </p>
          </div>
        )
      )}
    </div>
  );
};
